// src/components/Form/salaryreceptlist.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaPrint, FaEdit, FaTrashAlt, FaFileInvoiceDollar, FaArrowLeft, FaTimes, FaCheck, FaPlus, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import './CreateCustomerPage.css';

const SalaryReceiptList = ({ permissions: propPermissions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Global State
  const { user, baseUrl, configLoading, getHeaders } = useContext(UserContext);

  const [slips, setSlips] = useState([]);
  const [filteredSlips, setFilteredSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currency, setCurrency] = useState('₹');
  const [companyName, setCompanyName] = useState('My Company');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Permissions
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [dataScope, setDataScope] = useState("All Data"); // NEW: Data scope for filtering
  const [showPermModal, setShowPermModal] = useState(false);
  const [permModalMsg, setPermModalMsg] = useState("");

  useEffect(() => {
    if (configLoading) return;
    const fetchPermissions = async () => {
      try {
        setPermsLoading(true);
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          const roleRaw = userObj.role || userObj.UserType || '';
          const isAdminRole = checkIsAdmin(userObj);
          const isGroupAdminRole = checkIsGlobalAdmin(userObj);

          if (roleRaw) {
            const userEmail = userObj.email || '';
            const url = `${baseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}&email=${userEmail}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'salary_slip' || p.pageId === 'salary_list');
            
            if (isAdminRole || isGroupAdminRole) {
              setCanRead(true); setCanWrite(true); setCanDelete(true); setCanCreate(true);
              setDataScope("All Data");
            } else if (pagePerm) {
              setCanRead(pagePerm.canRead === true);
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
              setCanCreate(pagePerm.canCreate === true);
              setDataScope(pagePerm.dataAccess || pagePerm.dataScope || "All Data");
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setPermsLoading(false);
      }
    };
    fetchPermissions();
  }, [baseUrl, configLoading]);

  const itemsPerPage = 10;
  const formatCurrency = (val) => Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (configLoading) return;
    const currentBaseUrl = baseUrl || '';

    const fetchData = async () => {
      try {
        await Promise.all([ getSettings(), getCompany() ]);
        if (canRead) { await fetchSlips(); }
      } catch (err) { console.error(err); }
    };

    const getSettings = async () => {
      try {
        const res = await axios.get(`${currentBaseUrl}/api/settings`, { headers: getHeaders() });
        const map = { 'USD': '$', 'INR': '₹', 'EUR': '€', 'GBP': '£', 'AED': 'AED' };
        setCurrency(map[res.data.currency] || res.data.currency || '₹');
      } catch { }
    };

    const getCompany = async () => {
      try {
        const res = await axios.get(`${currentBaseUrl}/api/company-details`, { headers: getHeaders() });
        if (res.data.companyDetails?.length) setCompanyName(res.data.companyDetails.slice(-1)[0].restaurantName);
      } catch { }
    };

    fetchData();
  }, [baseUrl, configLoading, canRead, dataScope]);

  useEffect(() => {
    if (location.state?.fromSave) {
      setMessage("Salary slip saved successfully!");
      setTimeout(() => setMessage(''), 5000);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchSlips = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const uId = userObj.userId || userObj._id || userObj.id;
      const uRole = userObj.role;

      const res = await axios.get(`${baseUrl}/api/salary-slip`, {
        headers: getHeaders(),
        params: { userId: uId, id: uId, role: uRole }
      });

      const slipList = res.data || [];
      setSlips(slipList);
      setFilteredSlips(slipList);
    } catch (e) {
      console.error('Error fetching slips:', e);
      setError('Failed to fetch salary slips');
      setTimeout(() => setError(''), 5000);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    const filtered = slips.filter(s =>
      (s.employeeName || '').toLowerCase().includes(q) ||
      (s.employeeIdCode || '').toLowerCase().includes(q) ||
      (s.month || '').toLowerCase().includes(q)
    );
    setFilteredSlips(filtered);
    setCurrentPage(1);
  }, [searchQuery, slips]);

  const totalPages = Math.ceil(filteredSlips.length / itemsPerPage);
  const currentItems = filteredSlips.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleDelete = (id) => {
    if (!canDelete) {
      setPermModalMsg("You do not have permission to delete salary slips.");
      setShowPermModal(true);
      return;
    }
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${baseUrl}/api/salary-slip/${deletingId}`, { headers: getHeaders() });
      fetchSlips();
      setMessage("Salary slip deleted successfully!");
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError("Delete failed. Please try again.");
      setTimeout(() => setError(''), 5000);
    } finally {
      setShowDeleteConfirm(false);
      setDeletingId(null);
      setLoading(false);
    }
  };

  const handlePrint = async (id) => {
    try {
      const res = await axios.get(`${baseUrl}/api/salary-slip/${id}`, { headers: getHeaders() });
      const slip = res.data;
      const rosterDeductions = slip.rosterDeductions || slip.rosterAdditions || [];

      const w = window.open('', '_blank');
      w.document.write(`
        <html>
        <head>
          <title>Salary Slip - ${slip.employeeName}</title>
          <style>
             body { font-family: 'Arial', sans-serif; padding: 40px; }
             .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px;}
             .header h1 { margin: 0; font-size: 24px; text-transform: uppercase; }
             .sub-header { font-size: 14px; margin-top: 5px; }
             .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 20px; font-size: 13px; }
             .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
             table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 15px; }
             th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
             th { background: #f9f9f9; }
             .right { text-align: right; }
             .total-box { border: 2px solid #eee; padding: 15px; margin-top: 20px; }
             .net-pay { font-size: 18px; font-weight: bold; text-align: center; }
          </style>
        </head>
        <body>
           <div class="header">
              <h1>${companyName}</h1>
              <div class="sub-header">Salary Slip for ${slip.month}</div>
           </div>
           <div class="grid">
              <div>
                 <div class="row"><strong>Employee ID:</strong> ${slip.employeeIdCode || slip.employeeId}</div>
                 <div class="row"><strong>Name:</strong> ${slip.employeeName}</div>
                 <div class="row"><strong>Designation:</strong> ${slip.designation}</div>
                 <div class="row"><strong>Date of Joining:</strong> ${slip.postingDate}</div>
              </div>
              <div>
                 <div class="row"><strong>Status:</strong> ${slip.status}</div>
                 <div class="row"><strong>Payroll Mode:</strong> ${slip.modeOfPayment}</div>
                 <div class="row"><strong>Working Days:</strong> ${slip.workingDays}</div>
                 <div class="row"><strong>Paid Days:</strong> ${slip.paymentDays}</div>
              </div>
           </div>
           <h3>Earnings</h3>
           <table>
             <thead><tr><th>Component</th><th class="right">Amount</th></tr></thead>
             <tbody>
                ${(slip.earnings || []).map(e => `<tr><td>${e.component}</td><td class="right">${currency} ${formatCurrency(e.amount)}</td></tr>`).join('')}
                <tr style="font-weight:bold; background:#f0f8ff;">
                   <td>Total Gross Pay</td>
                   <td class="right">${currency} ${formatCurrency(slip.grossPay || slip.grossSalary)}</td>
                </tr>
             </tbody>
           </table>
           <h3>Deductions</h3>
           <table>
             <thead><tr><th>Component</th><th class="right">Amount</th></tr></thead>
             <tbody>
                ${(slip.leaveWithoutPay || 0) > 0 ? `<tr><td>Leave Without Pay</td><td class="right">${currency} ${formatCurrency(slip.leaveWithoutPay * (slip.dailyRate || 0))}</td></tr>` : ''}
                ${(slip.deductions || []).filter(d => !d.component.includes('Roster')).map(d => `<tr><td>${d.component}</td><td class="right">${currency} ${formatCurrency(d.amount)}</td></tr>`).join('')}
                ${rosterDeductions.map(r => `<tr><td>Roster: ${r.description || 'Roster Duty'} (${r.date || ''})</td><td class="right">${currency} ${formatCurrency(r.amount)}</td></tr>`).join('')}
                <tr style="font-weight:bold; background:#fff0f0;">
                   <td>Total Deductions</td>
                   <td class="right">${currency} ${formatCurrency(slip.totalDeductions)}</td>
                </tr>
             </tbody>
           </table>
           <div class="total-box">
              <div class="net-pay">Net Payable: ${currency} ${formatCurrency(slip.netPay)}</div>
              <div style="text-align:center; font-style:italic; margin-top:5px;">In Words: ${currency} ${formatCurrency(slip.netPay)} Only</div>
           </div>
           <div style="margin-top:50px; display:flex; justify-content:space-between; font-size:12px;">
              <div>Employee Signature</div>
              <div>Authorised Signatory</div>
           </div>
           <script>
             window.onload = function() { window.print(); }
           </script>
        </body>
        </html>
      `);
      w.document.close();
    } catch (e) {
      setError("Print failed");
      setTimeout(() => setError(''), 5000);
    }
  };

  if (permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <p style={{ color: '#604be8', fontSize: '18px', fontWeight: 'bold' }}>Loading Permissions...</p>
      </div>
    );
  }

  const userStr = localStorage.getItem('user');
  const userObj = userStr ? JSON.parse(userStr) : {};
  const isAdminRole = checkIsAdmin(userObj);

  if (!canRead && !isAdminRole && !permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#ef4444', fontSize: '18px', padding: '30px', background: '#fff', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view Salary Receipts.</p>
          <button className="save-btn" onClick={() => navigate('/admin')} style={{ marginTop: '20px', background: '#604be8' }}>Back to Admin</button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      
      {/* Notifications */}
      {error && (
        <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', background: '#fef2f2', color: '#ef4444', padding: '16px 24px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600', border: '1px solid #fee2e2', zIndex: 10001, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
          <FaExclamationTriangle style={{ fontSize: '20px' }} /> {error}
        </div>
      )}
      
      {message && (
        <div onClick={() => setMessage('')} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', padding: '40px 32px', borderRadius: '24px', width: '90%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'default' }}>
            <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: '#ecfdf5', color: '#10b981', border: '2px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px' }}>
              <FaCheckCircle />
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Success</h3>
            <p style={{ fontSize: '16px', color: '#64748b', margin: 0, fontWeight: '500' }}>{message}</p>
          </div>
        </div>
      )}

      {/* Permission Denied Modal */}
      {showPermModal && (
        <div className="add-modal-overlay" onClick={() => setShowPermModal(false)}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px', textAlign: 'center' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
              <span>Permission Denied</span>
              <button className="close-modal-btn" onClick={() => setShowPermModal(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#1e293b', fontWeight: '500' }}>{permModalMsg}</p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="save-btn" onClick={() => setShowPermModal(false)} style={{ background: '#64748b' }}>Understood</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confiramtion Modal */}
      {showDeleteConfirm && (
        <div className="add-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header" style={{ background: '#ef4444' }}>
              <span>Confirm Delete</span>
              <button className="close-modal-btn" onClick={() => setShowDeleteConfirm(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: '#1e293b', fontWeight: '500' }}>Are you sure you want to delete this salary slip? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="save-btn" onClick={confirmDelete} style={{ background: '#ef4444' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate('/admin')} className="header-back-btn print-hidden">
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaFileInvoiceDollar style={{ color: '#604be8' }} /> Salary Receipts <span style={{ fontSize: '18px', color: '#64748b', fontWeight: '600', marginLeft: '4px' }}>({filteredSlips.length})</span>
          </h1>
        </div>
        <div className="header-right">
          <div className="searchable-select" style={{ minWidth: '280px', marginRight: '16px' }}>
            <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search by name, ID, month..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: '44px', background: '#fff', border: '1px solid #e2e8f0' }}
            />
          </div>
          <button
            onClick={() => {
              if (!canCreate) {
                setPermModalMsg("You do not have permission to create salary slips.");
                setShowPermModal(true);
                return;
              }
              navigate('/salary-slip');
            }}
            className="save-btn" style={{ background: '#10b981', minWidth: '130px' }}
          >
            <FaPlus /> New Slip
          </button>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ padding: 0 }}>
          <div className="form-section-card theme-blue" style={{ borderRadius: '24px', padding: '24px', background: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            
            <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Month</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>Deductions</th>
                    <th style={{ textAlign: 'right' }}>Net Pay</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                     <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#604be8', fontWeight: '600' }}>Loading Slips...</td></tr>
                  ) : currentItems.length > 0 ? currentItems.map((s) => (
                    <tr key={s._id}>
                      <td style={{ fontWeight: '700', color: '#1e293b' }}>{s.employeeIdCode || s.employeeId}</td>
                      <td style={{ fontWeight: '600' }}>{s.employeeName}</td>
                      <td>{s.month}</td>
                      <td style={{ textAlign: 'right', fontWeight: '700', color: '#059669' }}>{currency} {formatCurrency(s.grossPay || s.grossSalary)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '700', color: '#dc2626' }}>{currency} {formatCurrency(s.totalDeductions)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '800', color: '#0f172a' }}>{currency} {formatCurrency(s.netPay)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '700',
                          backgroundColor: s.status === 'Submitted' ? '#dcfce7' : '#fef9c3',
                          color: s.status === 'Submitted' ? '#166534' : '#854d0e',
                          display: 'inline-block',
                          border: `1px solid ${s.status === 'Submitted' ? '#bbf7d0' : '#fef08a'}`
                        }}>
                          {s.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => {
                              if (!canWrite) {
                                setPermModalMsg("You do not have permission to edit salary slips.");
                                setShowPermModal(true);
                                return;
                              }
                              navigate(`/salary-slip?id=${s._id}`);
                            }}
                            className="save-btn"
                            style={{ background: '#f8fafc', color: '#604be8', border: '1px solid #e2e8f0', minWidth: 'auto', padding: '0 12px', height: '36px', boxShadow: 'none' }}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handlePrint(s._id)}
                            className="save-btn"
                            style={{ background: '#f8fafc', color: '#f59e0b', border: '1px solid #e2e8f0', minWidth: 'auto', padding: '0 12px', height: '36px', boxShadow: 'none' }}
                            title="Print"
                          >
                            <FaPrint />
                          </button>
                          <button
                            onClick={() => handleDelete(s._id)}
                            className="save-btn"
                            style={{ background: '#f8fafc', color: '#ef4444', border: '1px solid #e2e8f0', minWidth: 'auto', padding: '0 12px', height: '36px', boxShadow: 'none' }}
                            title="Delete"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="8" style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontWeight: '500' }}>
                        No salary slips found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredSlips.length > itemsPerPage && (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '24px' }}>
                {Array.from({ length: totalPages }, (_, k) => (
                  <button
                    key={k}
                    onClick={() => setCurrentPage(k + 1)}
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      border: 'none',
                      background: currentPage === k + 1 ? '#604be8' : '#f1f5f9',
                      color: currentPage === k + 1 ? '#fff' : '#475569',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxShadow: currentPage === k + 1 ? '0 4px 10px rgba(96, 75, 232, 0.3)' : 'none'
                    }}
                  >
                    {k + 1}
                  </button>
                ))}
              </div>
            )}
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryReceiptList;
